/**
 * Validates if a string is a properly formatted Matrix room ID.
 *
 * Matrix room IDs follow the format: !<localpart>:<server_name>
 * Server name can be:
 *   - DNS name: "matrix.org", "chat.example.com"
 *   - DNS name with port: "matrix.org:8448"
 *   - IPv4 with port: "192.168.1.1:8008"
 *   - IPv6 with port: "[::1]:8448", "[2001:db8::1]:8448"
 *   - Localhost: "localhost", "localhost:8008"
 *
 * Examples:
 *   Valid:
 *     - "!abc123:matrix.org"
 *     - "!room:localhost:8008"
 *     - "!room:192.168.1.1:8448"
 *     - "!room:[2001:db8::1]:8448"
 *   Invalid:
 *     - "" (empty)
 *     - "!" (missing parts)
 *     - "!room" (missing server)
 *     - "#room:server" (room alias, not ID)
 *
 * @param {string} roomId - The room ID to validate
 * @returns {boolean} True if valid Matrix room ID, false otherwise
 */
export const isValidRoomId = (roomId) => {
  // Type and nullish checks
  if (!roomId || typeof roomId !== 'string') {
    return false;
  }

  // Reject strings with leading/trailing whitespace
  if (roomId !== roomId.trim()) {
    return false;
  }

  // Prevent ReDoS: reject excessively long inputs
  // Max observed in Matrix: ~150 chars, use 255 as safe upper bound
  if (roomId.length > 255) {
    return false;
  }

  // Must start with ! sigil
  if (!roomId.startsWith('!')) {
    return false;
  }

  // Find the colon separator between localpart and server name
  const colonIndex = roomId.indexOf(':', 1);
  if (colonIndex === -1) {
    return false; // No colon separator
  }

  const localpart = roomId.slice(1, colonIndex);
  const serverName = roomId.slice(colonIndex + 1);

  // Validate localpart: alphanumeric, dots, hyphens, underscores (at least 1 char)
  if (!localpart || !/^[a-zA-Z0-9._-]+$/.test(localpart)) {
    return false;
  }

  // Validate server name
  // Server name can be:
  // 1. IPv6 with port: [ipv6]:port
  // 2. IPv4 with optional port: ipv4 or ipv4:port
  // 3. DNS name (including localhost) with optional port

  // IPv6 pattern: [hex:hex:...]:port or [hex:hex:...]
  const ipv6Pattern = /^\[([0-9a-fA-F:]+)\](:\d+)?$/;
  if (ipv6Pattern.test(serverName)) {
    return true;
  }

  // IPv4 pattern: num.num.num.num with optional :port
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}(:\d+)?$/;
  if (ipv4Pattern.test(serverName)) {
    return true;
  }

  // DNS name pattern (including localhost): labels separated by dots, optional :port
  // Each label: starts with alphanumeric, can contain hyphens, ends with alphanumeric
  // Single label (like "localhost") is also valid
  const dnsPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(:\d+)?$/;

  return dnsPattern.test(serverName);
};
